
import React, { useState, useMemo } from 'react';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { assessAssignment, analyzeRubric, BASE_SYSTEM_INSTRUCTION } from './services/geminiService';
import { assessWithDeepSeek } from './services/deepseekService';
import { assessWithOllama } from './services/ollamaService';
import { assessWithLlamaCpp } from './services/llamacppService';
import { assessWithAzure } from './services/azureService';
import { FileData, AssessmentResult, LLMProvider, StudentRecord, RubricAnalysis } from './types';
import ResultCard from './components/ResultCard';
import { parseTutorList, redactText } from './utils/redactionUtils';
import { extractTextFromFile } from './utils/fileParser';
import { downloadBatchAsExcel, downloadBatchReportsAsZip } from './utils/exportUtils';
import { AUTHORIZED_USERS } from './whitelist';

interface PreparedFile {
  file: FileData;
  redactedText: string;
  student: StudentRecord | null;
  status: 'ready' | 'redacted' | 'marking' | 'complete';
  wordCount: number;
}

const UON_LOGO_URL = "https://upload.wikimedia.org/wikipedia/en/3/3b/University_of_Nottingham_logo.svg";
const APP_VERSION = "v1.5-stable";

const PharmGradeMain: React.FC = () => {
  const { instance, accounts } = useMsal();
  
  // App State
  const [acceptedEula, setAcceptedEula] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [showRubricPreview, setShowRubricPreview] = useState(false);
  const [rubricText, setRubricText] = useState<string>('');
  const [previewingFile, setPreviewingFile] = useState<PreparedFile | null>(null);
  const [provider, setProvider] = useState<LLMProvider>('gemini');
  
  // Provider Model States
  const [geminiModel, setGeminiModel] = useState<string>('gemini-3-pro-preview');
  const [deepseekModel, setDeepseekModel] = useState<string>('deepseek-chat');
  const [azureDeployment, setAzureDeployment] = useState<string>('gpt-4o');
  const [ollamaModel, setOllamaModel] = useState<string>('llama3');
  const [ollamaBaseUrl, setOllamaBaseUrl] = useState<string>('http://localhost:11434');
  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [isFetchingOllama, setIsFetchingOllama] = useState(false);
  const [ollamaStatus, setOllamaStatus] = useState<'idle' | 'connected' | 'error'>('idle');
  const [llamacppBaseUrl, setLlamaCppBaseUrl] = useState<string>('http://127.0.0.1:8080');
  const [llamacppModel, setLlamaCppModel] = useState<string>('');
  const [llamacppModels, setLlamaCppModels] = useState<string[]>([]);
  const [isFetchingLlamaCpp, setIsFetchingLlamaCpp] = useState(false);
  const [llamacppStatus, setLlamaCppStatus] = useState<'idle' | 'connected' | 'error'>('idle');
  const [extraContext, setExtraContext] = useState<string>('');
  
  // Azure Specific States
  const [azureEndpoint, setAzureEndpoint] = useState<string>((process.env as any).VITE_AZURE_OPENAI_ENDPOINT || '');
  const [azureApiKey, setAzureApiKey] = useState<string>((process.env as any).VITE_AZURE_OPENAI_KEY || '');

  const deepseekApiKey = (process.env as any).DEEPSEEK_API_KEY || '';

  // Rubric State
  const [rubric, setRubric] = useState<FileData | null>(null);
  const [rubricAnalysis, setRubricAnalysis] = useState<RubricAnalysis | null>(null);
  const [isAnalysingRubric, setIsAnalysingRubric] = useState(false);

  // Tutor List State
  const [rawTutorList, setRawTutorList] = useState<StudentRecord[]>([]);
  const [tutorFileName, setTutorFileName] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  
  // Assignment State
  const [assignments, setAssignments] = useState<FileData[]>([]);
  const [preparedFiles, setPreparedFiles] = useState<Record<string, PreparedFile>>({});
  
  // Mark Processing
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [stopAtWordLimit, setStopAtWordLimit] = useState(false);
  const [wordLimit, setWordLimit] = useState<number>(1000);

  const handleLogout = () => instance.logoutRedirect({ postLogoutRedirectUri: "/" });

  // Whitelist Check
  const userEmail = accounts[0]?.username?.toLowerCase();
  const isAuthorized = AUTHORIZED_USERS.some(email => email.toLowerCase() === userEmail);

  if (!isAuthorized) {
    return (
      <div className="h-screen w-screen bg-uon-blue flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-white rounded-[4rem] shadow-2xl p-16 text-center space-y-8 border-t-[24px] border-rose-500">
          <img src={UON_LOGO_URL} alt="UoN" className="h-20 mx-auto opacity-50 grayscale" />
          <div className="space-y-4">
            <h1 className="heading-font text-4xl font-black text-uon-blue tracking-tighter uppercase">Access Denied</h1>
            <p className="text-slate-500 font-medium">
              You are signed in as <span className="text-uon-blue font-bold">{userEmail}</span>, but this account is not on the authorized whitelist for PharmGrade UK.
            </p>
            <p className="text-slate-400 text-sm italic">
              Please contact the system administrator to request access.
            </p>
          </div>
          <button 
            onClick={handleLogout} 
            className="w-full h-20 bg-slate-100 text-slate-600 rounded-[2rem] font-black text-xl hover:bg-rose-50 hover:text-rose-600 transition-all shadow-md"
          >
            Log Out & Switch Account
          </button>
        </div>
      </div>
    );
  }

  // Filtering Logic
  const uniqueYears = useMemo(() => {
    return Array.from(new Set(rawTutorList.map(s => s.year).filter(y => y))).sort();
  }, [rawTutorList]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(rawTutorList.map(s => s.course).filter(c => c))).sort();
  }, [rawTutorList]);

  const filteredTutorList = useMemo(() => {
    return rawTutorList.filter(s => {
      const yearMatch = !selectedYear || s.year === selectedYear;
      const courseMatch = selectedCourses.length === 0 || selectedCourses.includes(s.course);
      return yearMatch && courseMatch;
    });
  }, [rawTutorList, selectedYear, selectedCourses]);

  const handleRubricUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        const fileData = { name: file.name, base64, mimeType: file.type };
        setRubric(fileData);
        const text = await extractTextFromFile(base64, file.type);
        setRubricText(text);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTutorListUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const parsed = parseTutorList(base64);
        setRawTutorList(parsed);
        setTutorFileName(file.name);
        // Reset filters on new upload
        setSelectedYear('');
        setSelectedCourses([]);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCourse = (course: string) => {
    setSelectedCourses(prev => 
      prev.includes(course) ? prev.filter(c => c !== course) : [...prev, course]
    );
  };

  const refreshOllamaModels = async () => {
    setIsFetchingOllama(true);
    setOllamaStatus('idle');
    try {
      const models = await import('./services/ollamaService').then(m => m.fetchOllamaModels(ollamaBaseUrl));
      if (models.length > 0) {
        setOllamaModels(models);
        setOllamaStatus('connected');
        if (!models.includes(ollamaModel)) {
          setOllamaModel(models[0]);
        }
      } else {
        setOllamaStatus('error');
      }
    } catch (err: any) {
      console.error(err);
      setOllamaStatus('error');
      if (window.location.protocol === 'https:' && ollamaBaseUrl.startsWith('http://')) {
        setError("Connection blocked by browser (Mixed Content). Please see Help/FAQ Step 3.");
      } else {
        setError("Ollama connection failed. Check CORS settings in Help/FAQ.");
      }
    } finally {
      setIsFetchingOllama(false);
    }
  };

  const refreshLlamaCppModels = async () => {
    setIsFetchingLlamaCpp(true);
    setLlamaCppStatus('idle');
    try {
      const models = await import('./services/llamacppService').then(m => m.fetchLlamaCppModels(llamacppBaseUrl));
      if (models.length > 0) {
        setLlamaCppModels(models);
        setLlamaCppStatus('connected');
        if (!models.includes(llamacppModel)) {
          setLlamaCppModel(models[0]);
        }
      } else {
        setLlamaCppStatus('error');
      }
    } catch (err: any) {
      console.error(err);
      setLlamaCppStatus('error');
      if (window.location.protocol === 'https:' && llamacppBaseUrl.startsWith('http://')) {
        setError("Connection blocked by browser (Mixed Content). Use 127.0.0.1 or enable 'Insecure content' in browser settings.");
      } else {
        setError("llama.cpp connection failed. Is the server running?");
      }
    } finally {
      setIsFetchingLlamaCpp(false);
    }
  };

  const handleAssignmentsUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setAssignments((prev) => [...prev, { name: file.name, base64, mimeType: file.type }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const prepareForMarking = async () => {
    if (!rubric || assignments.length === 0) return;
    setIsPreparing(true);
    setError(null);
    try {
      const newPrepared: Record<string, PreparedFile> = {};
      for (const assignment of assignments) {
        const originalText = await extractTextFromFile(assignment.base64, assignment.mimeType);
        let { text: redactedText, student } = redactText(originalText, filteredTutorList);
        
        if (stopAtWordLimit && wordLimit > 0) {
          const words = redactedText.split(/\s+/);
          if (words.length > wordLimit) {
            redactedText = words.slice(0, wordLimit).join(' ') + "\n\n[SYSTEM NOTE: TEXT TRUNCATED AT WORD LIMIT]";
          }
        }

        newPrepared[assignment.name] = { 
          file: assignment, 
          redactedText, 
          student, 
          status: 'redacted', 
          wordCount: redactedText.split(/\s+/).length 
        };
      }
      setPreparedFiles(newPrepared);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsPreparing(false);
    }
  };

  const runMarking = async () => {
    if (provider === 'deepseek' && !deepseekApiKey) {
      setError("DeepSeek API Key is missing.");
      return;
    }
    if (provider === 'azure' && (!azureApiKey || !azureEndpoint)) {
      setError("Azure OpenAI credentials required.");
      return;
    }
    
    setIsProcessing(true);
    const newResults: AssessmentResult[] = [];
    try {
      const filesToProcess = Object.values(preparedFiles) as PreparedFile[];
      for (let i = 0; i < filesToProcess.length; i++) {
        setCurrentFileIndex(i);
        const prepared = filesToProcess[i];
        const anonymised: FileData = { 
          name: prepared.file.name,
          base64: btoa(unescape(encodeURIComponent(prepared.redactedText))), 
          mimeType: 'text/plain' 
        };
        
        let res: AssessmentResult;
        if (provider === 'gemini') {
          res = await assessAssignment(rubric!, anonymised, geminiModel, extraContext);
        } else if (provider === 'deepseek') {
          res = await assessWithDeepSeek(rubric!, anonymised, deepseekApiKey, deepseekModel, extraContext);
        } else if (provider === 'azure') {
          res = await assessWithAzure(rubric!, anonymised, azureApiKey, azureEndpoint, azureDeployment, extraContext);
        } else if (provider === 'llamacpp') {
          res = await assessWithLlamaCpp(rubric!, anonymised, { baseUrl: llamacppBaseUrl, model: llamacppModel }, extraContext);
        } else {
          res = await assessWithOllama(rubric!, anonymised, { baseUrl: ollamaBaseUrl, model: ollamaModel }, extraContext);
        }

        res.studentName = prepared.student?.name || "Unknown Student";
        res.studentID = prepared.student?.id || "ID Redacted";
        res.fileName = prepared.file.name;
        newResults.push(res);
      }
      setResults(newResults);
    } catch (err: any) {
      setError("Marking error: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="h-screen w-screen bg-white flex flex-col overflow-hidden text-slate-900 text-lg">
      {/* FAQ MODAL */}
      {showFaq && (
        <div className="fixed inset-0 bg-uon-blue/60 backdrop-blur-sm flex items-center justify-center p-6 z-[110]">
          <div className="max-w-4xl w-full bg-white rounded-[3rem] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh] custom-scrollbar relative">
            <div className="flex justify-between items-center border-b pb-6">
              <h2 className="text-3xl font-black text-uon-blue uppercase">System Guidance & FAQ</h2>
              <button onClick={() => setShowFaq(false)} className="text-slate-400 hover:text-rose-500 font-black">CLOSE</button>
            </div>
            <div className="space-y-8 text-base text-slate-700">
              <section className="space-y-2">
                <h3 className="font-black text-uon-sky uppercase text-xs tracking-widest">01. Data Privacy & Anonymisation</h3>
                <p>This tool performs <strong>browser-side redaction</strong>. When you upload a student list, names and IDs are cross-referenced locally. Only the <em>redacted</em> text is sent to the AI engine. No student identity is shared with the LLM provider.</p>
              </section>
              <section className="space-y-2">
                <h3 className="font-black text-uon-sky uppercase text-xs tracking-widest">02. Visual Content (Pro-Rata Logic)</h3>
                <p>LLMs are currently text-centric in this workflow. If your rubric assesses "Figures", "Chemical Structures", or "Diagrams", the AI flags these as <strong>isVisual: true</strong>. It provides a placeholder mark, which is then subtracted to provide an <strong>Adjusted Mark</strong> (Pro-Rata) based only on what the AI could verify.</p>
              </section>
              <section className="space-y-2">
                <h3 className="font-black text-uon-sky uppercase text-xs tracking-widest">03. UK English Standards</h3>
                <p>The system is hard-coded to require UK English (GPhC standards). It will specifically look for UK nomenclature (e.g., 'paracetamol' over 'acetaminophen') and Nottingham degree classification boundaries.</p>
              </section>
              <section className="space-y-2">
                <h3 className="font-black text-uon-sky uppercase text-xs tracking-widest">04. Local LLM (Ollama) Debugging</h3>
                <div className="space-y-4">
                  <p className="font-bold text-rose-600">The error "No 'Access-Control-Allow-Origin' header" means Ollama is rejecting the request from this website.</p>
                  
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <h4 className="font-black text-[10px] uppercase tracking-widest text-uon-blue">Step 1: Verify Configuration</h4>
                    <p className="text-xs">Run this in your terminal to check if Ollama is sending CORS headers:</p>
                    <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg text-[10px] font-mono overflow-x-auto">curl -I http://localhost:11434/api/tags</pre>
                    <p className="text-xs italic">If you don't see <code>Access-Control-Allow-Origin: *</code> in the output, it is NOT configured correctly.</p>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
                    <h4 className="font-black text-[10px] uppercase tracking-widest text-uon-blue">Step 2: Fix by Operating System</h4>
                    <div className="grid gap-4 text-xs">
                      <div>
                        <p className="font-bold underline">Linux (systemd):</p>
                        <ol className="list-decimal pl-4 space-y-1">
                          <li>Run: <code>sudo systemctl edit ollama.service</code></li>
                          <li>Add these lines:<br/><code>[Service]</code><br/><code>Environment="OLLAMA_ORIGINS=*"</code></li>
                          <li>Save and exit.</li>
                          <li>Run: <code>sudo systemctl daemon-reload</code></li>
                          <li>Run: <code>sudo systemctl restart ollama</code></li>
                        </ol>
                      </div>
                      <div>
                        <p className="font-bold underline">macOS:</p>
                        <ol className="list-decimal pl-4 space-y-1">
                          <li>Quit the Ollama app completely.</li>
                          <li>Open Terminal and run: <code>launchctl setenv OLLAMA_ORIGINS "*"</code></li>
                          <li>Restart the Ollama app.</li>
                        </ol>
                      </div>
                      <div>
                        <p className="font-bold underline">Manual Test (All Platforms):</p>
                        <ol className="list-decimal pl-4 space-y-1">
                          <li>Stop any running Ollama process.</li>
                          <li>Open a terminal and run: <code>OLLAMA_ORIGINS="*" ollama serve</code></li>
                          <li>Keep that terminal open and try the <strong>Fetch</strong> button again.</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="bg-rose-50 p-4 rounded-xl border border-rose-200 space-y-2">
                    <h4 className="font-black text-[10px] uppercase tracking-widest text-rose-600">Step 3: Mixed Content (HTTPS vs HTTP)</h4>
                    <p className="text-xs">Since this app is on <strong>HTTPS</strong>, browsers block <strong>HTTP</strong> requests to localhost for security.</p>
                    <p className="text-xs font-bold">Solution: Click the lock icon in your browser address bar → Site Settings → Set "Insecure content" to "Allow".</p>
                  </div>
                </div>
              </section>
              <section className="space-y-2">
                <h3 className="font-black text-uon-sky uppercase text-xs tracking-widest">05. Academic Moderation</h3>
                <p className="font-bold text-rose-600">This tool is an assistant, NOT a marker. All generated marks are DRAFT and MUST be moderated by a human academic lead before any student receives feedback.</p>
              </section>
            </div>
            <button onClick={() => setShowFaq(false)} className="w-full py-5 bg-uon-blue text-white rounded-2xl font-black hover:bg-uon-sky transition-all">Understood</button>
          </div>
        </div>
      )}

      {/* PROMPT PREVIEW */}
      {showPromptPreview && (
        <div className="fixed inset-0 bg-uon-blue/60 backdrop-blur-sm flex items-center justify-center p-6 z-[110]">
          <div className="max-w-2xl w-full bg-white rounded-[3rem] shadow-2xl p-10 space-y-6 overflow-y-auto max-h-[85vh] custom-scrollbar relative">
            <h2 className="text-2xl font-black text-uon-blue uppercase tracking-tighter">AI System Instruction</h2>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 font-mono text-xs leading-relaxed text-slate-700 whitespace-pre-wrap">
              {BASE_SYSTEM_INSTRUCTION}
              {extraContext && `\n\n[USER CONTEXT ADDED]:\n${extraContext}`}
            </div>
            <button onClick={() => setShowPromptPreview(false)} className="w-full py-4 bg-uon-blue text-white rounded-2xl font-black hover:bg-uon-sky transition-all">Close</button>
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className="h-28 border-b border-slate-200 px-8 flex items-center justify-between shrink-0 bg-white z-50">
        <div className="flex items-center gap-10">
          <img src={UON_LOGO_URL} alt="UoN" className="h-16 w-auto" />
          <h1 className="heading-font text-4xl font-black text-uon-blue">PharmGrade <span className="text-uon-sky">UK</span></h1>
        </div>
        <div className="hidden md:flex flex-col items-center">
          <div className="bg-rose-50 border border-rose-200 px-4 py-1.5 rounded-full flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse"></span>
            <span className="text-[10px] font-black text-rose-600 uppercase tracking-widest">Human Moderation Mandatory</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setShowFaq(true)} className="px-5 py-3 rounded-xl border-2 border-slate-200 text-slate-600 hover:border-uon-sky hover:text-uon-sky transition-all font-black text-xs uppercase tracking-widest">Help / FAQ</button>
          <button onClick={handleLogout} className="px-5 py-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-rose-50 transition-all font-black text-xs uppercase tracking-widest">Sign Out</button>
        </div>
      </header>

      {/* MAIN LAYOUT */}
      <main className="flex-1 flex flex-col lg:flex-row overflow-hidden bg-slate-50">
        <aside className="w-full lg:w-[460px] bg-white border-r border-slate-200 flex flex-col h-full shadow-xl z-40">
          <div className="p-8 space-y-8 overflow-y-auto flex-1 custom-scrollbar">
            <h2 className="text-2xl font-black text-uon-blue border-b-4 border-uon-light pb-4 uppercase">Control Panel</h2>
            
            <div className="space-y-4">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">01. AI Engine & Model</label>
              <div className="grid grid-cols-5 gap-2 p-1 bg-slate-100 rounded-xl mb-2">
                {['gemini', 'azure', 'deepseek', 'ollama', 'llamacpp'].map((p) => (
                  <button 
                    key={p}
                    onClick={() => setProvider(p as LLMProvider)} 
                    className={`py-2 rounded-lg text-[9px] font-black capitalize transition-all ${provider === p ? 'bg-white shadow text-uon-blue' : 'text-slate-400'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* MODEL SELECTORS */}
              {provider === 'gemini' && (
                <select value={geminiModel} onChange={(e) => setGeminiModel(e.target.value)} className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50 text-uon-blue">
                  <option value="gemini-3-pro-preview">Gemini 3 Pro (Academic High-Reasoning)</option>
                  <option value="gemini-3-flash-preview">Gemini 3 Flash (Fast Batching)</option>
                </select>
              )}
              {provider === 'deepseek' && (
                <select value={deepseekModel} onChange={(e) => setDeepseekModel(e.target.value)} className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50 text-uon-blue">
                  <option value="deepseek-chat">DeepSeek Chat (Standard)</option>
                  <option value="deepseek-reasoner">DeepSeek Reasoner (V3 - Thinking)</option>
                </select>
              )}
              {provider === 'azure' && (
                <input 
                  type="text" 
                  placeholder="Azure Deployment Name (e.g. gpt-4o)" 
                  value={azureDeployment} 
                  onChange={(e) => setAzureDeployment(e.target.value)}
                  className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50"
                />
              )}
              {provider === 'ollama' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input 
                        type="text" 
                        placeholder="Ollama URL (e.g. http://localhost:11434)" 
                        value={ollamaBaseUrl} 
                        onChange={(e) => setOllamaBaseUrl(e.target.value)}
                        className={`w-full p-3 rounded-xl border-2 text-xs font-black bg-slate-50 ${ollamaStatus === 'connected' ? 'border-uon-green' : ollamaStatus === 'error' ? 'border-rose-300' : 'border-slate-200'}`}
                      />
                      {ollamaStatus !== 'idle' && (
                        <div className={`absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${ollamaStatus === 'connected' ? 'bg-uon-green' : 'bg-rose-500'}`}></div>
                      )}
                    </div>
                    <button 
                      onClick={refreshOllamaModels}
                      disabled={isFetchingOllama}
                      className="px-4 py-2 bg-uon-blue text-white rounded-xl text-[10px] font-black uppercase hover:bg-uon-sky disabled:opacity-50"
                    >
                      {isFetchingOllama ? '...' : 'Fetch'}
                    </button>
                  </div>
                  {ollamaModels.length > 0 ? (
                    <select 
                      value={ollamaModel} 
                      onChange={(e) => setOllamaModel(e.target.value)} 
                      className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50 text-uon-blue"
                    >
                      {ollamaModels.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : (
                    <input 
                      type="text" 
                      placeholder="Model Name (e.g. llama3)" 
                      value={ollamaModel} 
                      onChange={(e) => setOllamaModel(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50"
                    />
                  )}
                  {window.location.protocol === 'https:' && ollamaBaseUrl.startsWith('http://') && (
                    <p className="text-[9px] text-rose-500 font-bold px-1">
                      ⚠️ Mixed Content: Browser may block HTTPS to HTTP. Use 127.0.0.1 or enable 'Insecure content' in browser settings.
                    </p>
                  )}
                  <p className="text-[9px] text-slate-400 font-bold italic px-1">
                    Note: Ensure OLLAMA_ORIGINS="*" is set in your environment.
                  </p>
                </div>
              )}
              {provider === 'llamacpp' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="llama.cpp URL (e.g. http://127.0.0.1:8080)"
                        value={llamacppBaseUrl}
                        onChange={(e) => setLlamaCppBaseUrl(e.target.value)}
                        className={`w-full p-3 rounded-xl border-2 text-xs font-black bg-slate-50 ${llamacppStatus === 'connected' ? 'border-uon-green' : llamacppStatus === 'error' ? 'border-rose-300' : 'border-slate-200'}`}
                      />
                      {llamacppStatus !== 'idle' && (
                        <div className={`absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${llamacppStatus === 'connected' ? 'bg-uon-green' : 'bg-rose-500'}`}></div>
                      )}
                    </div>
                    <button
                      onClick={refreshLlamaCppModels}
                      disabled={isFetchingLlamaCpp}
                      className="px-4 py-2 bg-uon-blue text-white rounded-xl text-[10px] font-black uppercase hover:bg-uon-sky disabled:opacity-50"
                    >
                      {isFetchingLlamaCpp ? '...' : 'Fetch'}
                    </button>
                  </div>
                  {llamacppModels.length > 0 ? (
                    <select
                      value={llamacppModel}
                      onChange={(e) => setLlamaCppModel(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50 text-uon-blue"
                    >
                      {llamacppModels.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      placeholder="Model Name (e.g. qwen2.5-3b-instruct-q4_k_m)"
                      value={llamacppModel}
                      onChange={(e) => setLlamaCppModel(e.target.value)}
                      className="w-full p-3 rounded-xl border-2 border-slate-200 text-xs font-black bg-slate-50"
                    />
                  )}
                  {window.location.protocol === 'https:' && llamacppBaseUrl.startsWith('http://') && (
                    <p className="text-[9px] text-rose-500 font-bold px-1">
                      ⚠️ Mixed Content: Browser may block HTTPS to HTTP. Use 127.0.0.1 or enable 'Insecure content' in browser settings.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4">
               <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">02. Academic Files</label>
               <label className={`flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all bg-slate-50 hover:border-uon-sky ${rubric ? 'border-uon-green bg-uon-green/5' : 'border-slate-200'}`}>
                <input type="file" accept=".pdf,.json" className="hidden" onChange={handleRubricUpload} />
                <span className="text-xs font-black text-slate-700 truncate max-w-full">{rubric ? `Rubric: ${rubric.name}` : 'Upload PDF/JSON Rubric'}</span>
              </label>

              <label className={`flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all bg-slate-50 hover:border-uon-sky ${tutorFileName ? 'border-uon-green bg-uon-green/5' : 'border-slate-200'}`}>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleTutorListUpload} />
                <span className="text-xs font-black text-slate-700 truncate max-w-full">{tutorFileName ? `Students: ${tutorFileName}` : 'Upload Student Excel'}</span>
              </label>

              {/* Subset Filtering UI */}
              {rawTutorList.length > 0 && (
                <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-uon-blue">Subset Student List</label>
                    <span className="text-[10px] font-black bg-uon-blue text-white px-2 py-0.5 rounded-full">{filteredTutorList.length} Active</span>
                  </div>
                  
                  <div className="space-y-3">
                    <select 
                      value={selectedYear} 
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="w-full p-2 rounded-lg border border-slate-300 text-[10px] font-black uppercase"
                    >
                      <option value="">All Years of Study</option>
                      {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>

                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase text-slate-400 tracking-tighter">Course Codes</label>
                      <div className="max-h-24 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white custom-scrollbar space-y-1">
                        {uniqueCourses.map(course => (
                          <label key={course} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors">
                            <input 
                              type="checkbox" 
                              checked={selectedCourses.includes(course)}
                              onChange={() => toggleCourse(course)}
                              className="w-3 h-3 rounded text-uon-sky focus:ring-uon-sky border-slate-300"
                            />
                            <span className="text-[9px] font-black text-slate-600 truncate">{course}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-xl bg-slate-50 border-slate-200 hover:border-uon-sky cursor-pointer transition-all">
                <input type="file" multiple accept=".pdf,.docx" className="hidden" onChange={handleAssignmentsUpload} />
                <span className="text-xs font-black text-slate-700">Add Student Papers ({assignments.length})</span>
              </label>
            </div>

            <div className="space-y-4 bg-uon-light/10 p-4 rounded-2xl border border-uon-light/30">
              <label className="text-[11px] font-black uppercase tracking-widest text-uon-blue flex justify-between">
                03. Module Context 
                <button onClick={() => setShowPromptPreview(true)} className="text-[9px] underline">View Prompt</button>
              </label>
              
              <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-uon-light/30">
                <input 
                  type="checkbox" 
                  id="stopAtLimit"
                  checked={stopAtWordLimit}
                  onChange={(e) => setStopAtWordLimit(e.target.checked)}
                  className="w-4 h-4 rounded text-uon-sky focus:ring-uon-sky border-slate-300"
                />
                <label htmlFor="stopAtLimit" className="text-[10px] font-black uppercase text-uon-blue cursor-pointer flex-1">Stop reading at limit</label>
                {stopAtWordLimit && (
                  <input 
                    type="number" 
                    value={wordLimit}
                    onChange={(e) => setWordLimit(parseInt(e.target.value) || 0)}
                    className="w-20 p-1 rounded border border-slate-200 text-[10px] font-black text-center"
                    placeholder="Limit"
                  />
                )}
              </div>

              <textarea 
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder="e.g. 'Year 2 Clinical Pharmacy. Focus on GPhC outcome 3.1...'"
                className="w-full p-4 rounded-xl border-2 border-slate-200 bg-white text-xs font-medium focus:border-uon-sky outline-none min-h-[100px] resize-none"
              />
            </div>
          </div>

          <div className="p-8 border-t border-slate-100 bg-slate-50/50">
            {Object.keys(preparedFiles).length === 0 ? (
              <button onClick={prepareForMarking} disabled={!rubric || assignments.length === 0 || isPreparing} className="w-full h-16 bg-uon-blue text-white rounded-2xl font-black text-lg shadow-xl hover:bg-uon-sky disabled:opacity-20 transition-all">
                {isPreparing ? 'REDACTING...' : 'PREPARE FOR BATCH'}
              </button>
            ) : (
              <button onClick={runMarking} disabled={isProcessing} className="w-full h-16 bg-uon-sky text-white rounded-2xl font-black text-lg shadow-xl hover:bg-uon-blue transition-all">
                {isProcessing ? `MARKING... (${currentFileIndex+1}/${assignments.length})` : `START ASSESSMENT (${assignments.length})`}
              </button>
            )}
            {error && <p className="text-rose-500 text-[10px] font-black text-center mt-3">{error}</p>}
          </div>
        </aside>

        <section className="flex-1 overflow-y-auto p-12 custom-scrollbar">
          {results.length === 0 && !isProcessing && !isPreparing && (
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="space-y-2">
                <h2 className="text-6xl font-black text-uon-blue tracking-tighter uppercase">Academic Batch</h2>
                <p className="text-slate-400 font-bold text-sm tracking-widest uppercase">Draft Feedback Generator • UK Higher Education</p>
              </div>
              
              {Object.keys(preparedFiles).length > 0 ? (
                <div className="grid gap-8">
                  {(Object.values(preparedFiles) as PreparedFile[]).map((p, i) => (
                    <div key={i} className="bg-white p-6 rounded-3xl border border-slate-200 flex justify-between items-center shadow-sm">
                      <div>
                        <h4 className="font-black text-uon-blue text-lg">{p.file.name}</h4>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {p.wordCount} words extracted • {p.student ? `Matched: ${p.student.name}` : 'Unknown Student'}
                        </span>
                      </div>
                      <button onClick={() => setPreviewingFile(p)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-black text-[10px] uppercase hover:bg-uon-gold transition-all">Review Anonymisation</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-96 border-4 border-dashed border-slate-200 rounded-[4rem] flex flex-col items-center justify-center text-slate-300 font-black uppercase tracking-widest text-center space-y-4">
                  <svg className="h-16 w-16 opacity-10" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" /></svg>
                  <p>Upload files to start batch assessment</p>
                </div>
              )}
            </div>
          )}

          {(isProcessing || isPreparing) && (
            <div className="h-full flex flex-col items-center justify-center space-y-8">
              <div className="relative h-24 w-24 border-8 border-slate-100 border-t-uon-sky rounded-full animate-spin"></div>
              <div className="text-center space-y-2">
                <p className="text-2xl font-black text-uon-blue uppercase tracking-widest">{isPreparing ? 'Redacting personal data...' : `Marking Submission ${currentFileIndex + 1} of ${assignments.length}`}</p>
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Powered by {provider} {provider === 'gemini' ? geminiModel : ''}</p>
              </div>
            </div>
          )}

          {results.length > 0 && !isProcessing && (
            <div className="max-w-6xl mx-auto space-y-16 pb-20">
               <div className="flex justify-between items-end border-b-8 border-slate-200 pb-10">
                  <div className="space-y-2">
                    <h2 className="text-7xl font-black text-uon-blue tracking-tighter uppercase">Results</h2>
                    <p className="text-rose-600 font-black text-xs uppercase tracking-widest flex items-center gap-2">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                      All results below are DRAFT and require academic moderation
                    </p>
                  </div>
                  <div className="flex gap-4">
                    <button onClick={() => downloadBatchAsExcel(results)} className="bg-white border-2 border-slate-200 px-6 py-3 rounded-xl font-black hover:border-uon-sky transition-all text-xs uppercase tracking-widest">Excel Export</button>
                    <button onClick={() => downloadBatchReportsAsZip(results, 'pdf')} className="bg-uon-blue text-white px-6 py-3 rounded-xl font-black hover:bg-uon-sky transition-all text-xs uppercase tracking-widest">ZIP (All PDFs)</button>
                  </div>
               </div>
               <div className="grid gap-16">
                 {results.map((r, i) => <ResultCard key={i} result={r} />)}
               </div>
            </div>
          )}
        </section>
      </main>

      {/* ORIGINAL EULA POPUP */}
      {!acceptedEula && (
        <div className="fixed inset-0 bg-uon-blue flex items-center justify-center p-6 z-[200]">
          <div className="max-w-3xl w-full bg-white rounded-[4rem] p-16 space-y-10 shadow-2xl border-b-[32px] border-uon-sky overflow-hidden relative">
             <div className="absolute top-0 right-0 p-12 text-uon-light/10 select-none pointer-events-none">
                <svg className="h-64 w-64 rotate-12" fill="currentColor" viewBox="0 0 20 20"><path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10.196a7.969 7.969 0 013.5-.804c1.255 0 2.443.29 3.5.804V4.804zM11 4.804A7.968 7.968 0 0114.5 4c1.255 0 2.443.29 3.5.804v10.196a7.969 7.969 0 00-3.5-.804c-1.255 0-2.443.29-3.5.804V4.804z" /></svg>
             </div>
             <div className="space-y-2">
                <h2 className="text-5xl font-black text-uon-blue uppercase tracking-tighter">Academic Integrity & Use Policy</h2>
                <p className="text-uon-sky font-black uppercase tracking-widest text-xs">PharmGrade UK Assessment Assistant</p>
             </div>
             <div className="space-y-6 text-base font-medium text-slate-600 leading-relaxed relative z-10">
               <p>By using this application, you acknowledge and agree to the following terms regarding academic quality and data protection:</p>
               <ul className="space-y-4 list-disc pl-5 font-bold">
                 <li><strong>Moderation:</strong> This tool generates <em>DRAFT</em> feedback. In accordance with the UoN Quality Manual, all marks MUST be moderated and verified by a human academic lead before release.</li>
                 <li><strong>Privacy:</strong> Redaction of student identifiers happens locally in your browser. Ensure your Student Excel list is up-to-date for accurate anonymisation.</li>
                 <li><strong>Data Use:</strong> You confirm that you have the appropriate permissions to process these documents under University of Nottingham research or administrative guidelines.</li>
                 <li><strong>GPhC Compliance:</strong> Marks are provided as indicative drafts for MPharm/PharmSci undergraduate modules only.</li>
               </ul>
             </div>
             <button onClick={() => setAcceptedEula(true)} className="w-full py-8 bg-uon-blue text-white rounded-[2rem] font-black text-2xl hover:bg-uon-sky transition-all shadow-xl">I Accept & Confirm Moderation Duty</button>
          </div>
        </div>
      )}

      {/* PREVIEW REDACTION MODAL */}
      {previewingFile && (
        <div className="fixed inset-0 bg-uon-blue/80 backdrop-blur-sm flex items-center justify-center p-6 z-[120]">
          <div className="max-w-4xl w-full bg-white rounded-[3rem] p-10 space-y-6 overflow-y-auto max-h-[85vh] relative shadow-2xl">
            <h2 className="text-2xl font-black text-uon-blue uppercase">Local Redaction Check</h2>
            <p className="text-xs font-bold text-slate-400">This text is exactly what will be sent to the AI API. Personal identifiers should be replaced with placeholders like [NAME_REDACTED].</p>
            <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 font-mono text-xs leading-relaxed whitespace-pre-wrap text-slate-700">
              {previewingFile.redactedText}
            </div>
            <button onClick={() => setPreviewingFile(null)} className="w-full py-5 bg-uon-blue text-white rounded-2xl font-black hover:bg-uon-sky transition-all">Looks Safe</button>
          </div>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const { instance } = useMsal();
  return (
    <>
      <AuthenticatedTemplate><PharmGradeMain /></AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <div className="h-screen w-screen bg-uon-blue flex items-center justify-center p-6">
           <div className="max-w-2xl w-full bg-white rounded-[4rem] shadow-2xl p-16 text-center space-y-12 border-b-[24px] border-uon-sky">
             <img src={UON_LOGO_URL} alt="UoN" className="h-24 mx-auto" />
             <div className="space-y-4">
              <h1 className="heading-font text-6xl font-black text-uon-blue tracking-tighter">PharmGrade <span className="text-uon-sky">UK</span></h1>
              <p className="text-xs font-black uppercase tracking-[0.4em] text-slate-400">{APP_VERSION}</p>
             </div>
             <button onClick={() => instance.loginRedirect(loginRequest)} className="w-full h-24 bg-uon-blue text-white rounded-[2rem] font-black text-2xl hover:bg-uon-sky transition-all shadow-xl">Sign-in with University ID</button>
           </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};

export default App;
